import { clearAllStorageData, getToken } from "@/utils/storage";
export interface DecodedToken {
  exp: number;
  iat: number;
  sub: string;
  email?: string;
  // Add other JWT payload fields as needed
}

function decodeJwtPayload<T = unknown>(token: string): T {
  const [, payloadPart] = token.split('.');
  if (!payloadPart) {
    throw new Error('Invalid JWT format');
  }

  // Convert base64url -> base64 and add padding
  const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const json = atob(padded);
  return JSON.parse(json) as T;
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = decodeJwtPayload<DecodedToken>(token);
    const currentTime = Math.floor(Date.now() / 1000);

    // Check if token is expired (with 30 second buffer)
    return payload.exp < (currentTime + 30);
  } catch (error) {
    console.error('Error decoding token:', error);
    return true; // Consider invalid tokens as expired
  }
};

/**
 * Canonical "is this user currently validly authenticated?" check.
 *
 * Reuses the app's existing expiry authority (`isTokenExpired`, 30s buffer,
 * malformed => expired) rather than trusting `access_token` presence. This is
 * the single source of truth for the Trial-is-guest-only redirect decision and
 * deliberately mirrors the logic used by AuthContext / checkTokenAndRedirect.
 */
export const isValidlyAuthenticated = (): boolean => {
  const token = getToken();
  return !!token && !isTokenExpired(token);
};

export const getTokenExpirationTime = (token: string): number | null => {
  try {
    const payload = decodeJwtPayload<DecodedToken>(token);
    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Error getting token expiration:', error);
    return null;
  }
};

export const logout = (): void => {
  // Use our safe clear that keeps trial_banner_dismissed_v2
  clearAllStorageExceptPreserved();
  window.location.href = '/login';
};

export const checkTokenAndRedirect = (): boolean => {
  const token = localStorage.getItem('access_token');

  if (!token || isTokenExpired(token)) {
    logout();
    return false;
  }

  return true;
};
